import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth-store';
import { useTenantStore } from '@/stores/tenant-store';
import { getEnhancedTenantHeaders } from './utils';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Add tenant header if available
    const tenant = useTenantStore.getState().tenant;
    if (tenant?.slug) {
      config.headers['X-Tenant'] = tenant.slug;
    } else {
      // Fallback to enhanced tenant headers for production
      const enhancedHeaders = getEnhancedTenantHeaders();
      if (enhancedHeaders['X-Tenant']) {
        config.headers['X-Tenant'] = enhancedHeaders['X-Tenant'];
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const { response } = error;
    
    // Handle 401 errors (token expired/invalid)
    if (response?.status === 401) {
      console.log('401 error detected, logging out user');
      
      // Clear auth state
      useAuthStore.getState().clearAuth();
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to check if error is a token invalidation error
export const isTokenInvalidationError = (error: AxiosError): boolean => {
  const { response } = error;
  
  if (response?.status === 401) {
    return true;
  }
  
  const errorData = response?.data as any;
  
  // Check for specific error formats
  if (errorData?.code === 'token_not_valid' || 
      errorData?.detail?.includes('Invalid token') || 
      errorData?.detail?.includes('Authentication credentials') ||
      errorData?.detail?.includes('Given token not valid for any token type')) {
    return true;
  }
  
  // Check for token expired messages in the messages array
  if (errorData?.messages && Array.isArray(errorData.messages)) {
    for (const message of errorData.messages) {
      if (message.message?.includes('Token is expired') || 
          message.message?.includes('token_not_valid')) {
        return true;
      }
    }
  }
  
  return false;
};

// Helper function to handle token invalidation
export const handleTokenInvalidation = (): void => {
  console.log('Handling token invalidation');
  
  // Clear auth state
  useAuthStore.getState().clearAuth();
  
  // Redirect to login page
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

export default apiClient; 